import axios from 'axios';

const API_BASE = import.meta.env.VITE_API_URL || 'http://localhost:8000/api';

const api = axios.create({ baseURL: API_BASE });

// Attach JWT token to every request
api.interceptors.request.use(config => {
  const token = localStorage.getItem('access_token');
  if (token) config.headers.Authorization = `Bearer ${token}`;
  return config;
});

// Auto-refresh token on 401
api.interceptors.response.use(
  res => res,
  async err => {
    const original = err.config;
    if (err.response?.status === 401 && !original._retry) {
      original._retry = true;
      try {
        const refresh = localStorage.getItem('refresh_token');
        const { data } = await axios.post(`${API_BASE}/auth/refresh/`, { refresh });
        localStorage.setItem('access_token', data.access);
        original.headers.Authorization = `Bearer ${data.access}`;
        return api(original);
      } catch {
        localStorage.clear();
        window.location.href = '/login';
      }
    }
    return Promise.reject(err);
  }
);

// ── Auth ──────────────────────────────────────────────────────────────────────
export const authAPI = {
  login: (username, password) =>
    api.post('/auth/login/', { username, password }),
  getMe: () => api.get('/users/me/'),
};

// ── Cases ─────────────────────────────────────────────────────────────────────
export const casesAPI = {
  list: (params) => api.get('/cases/', { params }),
  detail: (id) => api.get(`/cases/${id}/`),
  create: (data) => api.post('/cases/', data),
  update: (id, data) => api.patch(`/cases/${id}/`, data),
  delete: (id) => api.delete(`/cases/${id}/`),
  fetchCnr: (cnr) => api.get('/cases/fetch_cnr/', { params: { cnr } }),
  syncCase: (id) => api.post(`/cases/${id}/sync/`),
  upcomingHearings: (days = 7) => api.get('/cases/upcoming_hearings/', { params: { days } }),
  dashboardStats: () => api.get('/cases/dashboard_stats/'),
  generateSummary: (id) => api.get(`/cases/${id}/summary/`),
};

// ── Activities ────────────────────────────────────────────────────────────────
export const activitiesAPI = {
  list: (caseId) => api.get(`/cases/${caseId}/activities/`),
  create: (caseId, data) => api.post(`/cases/${caseId}/activities/`, data),
};

// ── Documents ─────────────────────────────────────────────────────────────────
export const documentsAPI = {
  list: (caseId) => api.get(`/cases/${caseId}/documents/`),
  upload: (caseId, formData) =>
    api.post(`/cases/${caseId}/documents/`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    }),
  delete: (caseId, id) => api.delete(`/cases/${caseId}/documents/${id}/`),
};

// ── Notifications ─────────────────────────────────────────────────────────────
export const notificationsAPI = {
  list: (params) => api.get('/notifications/', { params }),
  sendManual: (caseId) => api.post('/notifications/send_manual/', { case_id: caseId }),
  runScheduler: () => api.post('/notifications/run_scheduler/'),
};

// ── Reports ───────────────────────────────────────────────────────────────────
export const reportsAPI = {
  dashboard: () => api.get('/reports/dashboard/'),
  sendDaily: () => api.post('/reports/send-daily/'),
};

// ── Users ─────────────────────────────────────────────────────────────────────
export const usersAPI = {
  list: () => api.get('/users/'),
  lawyers: () => api.get('/users/lawyers/'),
  create: (data) => api.post('/users/', data),
  update: (id, data) => api.patch(`/users/${id}/`, data),
};

export default api;
